import React, { type FC } from 'react';
import { CardContainer } from 'layout/Card';
import { type EnergyAudit } from 'interfaces/EnergyAudit.interface';
import EnergyAuditMethodologyTab from './tabs/MethodologyTab';
import Skeleton from 'react-loading-skeleton';
import NumberedBlockList from './lists/NumberedBlockList';
import BulletBlockList from './lists/BulletBlockList';
import EnergyAuditRiskComplianceTab from './tabs/RiskComplianceTab';
import EnergyAuditActionPlanTab from './tabs/ActionPlanTab';
import EnergyAuditSubsidiesTab from './tabs/SubsidiesTab';
import EnergyAuditBenefitsTab from './tabs/BenefitsTab';

interface EnergyAuditTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  data?: EnergyAudit;
  tabs: {
    label: string;
    key: string;
    icon?: (active: boolean) => JSX.Element;
  }[];
  isFetching?: boolean;
}

function renderTabContent({
  activeTab,
  data,
}: {
  activeTab: string;
  data?: EnergyAudit;
}): JSX.Element {
  if (!data) {
    return <div className="">{'No data available.'}</div>;
  }

  const specialTabs: Record<string, (data: EnergyAudit) => JSX.Element> = {
    methodology: (data) => <EnergyAuditMethodologyTab data={data} />,
    risk_compliance: (data) => <EnergyAuditRiskComplianceTab data={data} />,
    action_plan: (data) => <EnergyAuditActionPlanTab data={data} />,
    subsidies: (data) => <EnergyAuditSubsidiesTab data={data} />,
    benefits: (data) => <EnergyAuditBenefitsTab data={data} />,
  };

  if (specialTabs[activeTab]) {
    return specialTabs[activeTab](data);
  }

  const tabData = data[activeTab as keyof typeof data] as any;
  const blocks = tabData?.blocks;
  if (Array.isArray(blocks) && blocks.length > 0) {
    if (
      blocks.every(
        (block: any) =>
          typeof block.type === 'string' &&
          block.type.toLowerCase().includes('bullet')
      )
    ) {
      return <BulletBlockList blocks={blocks} />;
    }
    if (
      blocks.every(
        (block: any) =>
          typeof block.type === 'string' &&
          block.type.toLowerCase().includes('numbered')
      )
    ) {
      return <NumberedBlockList blocks={blocks} />;
    }
  }
  return <div>{'No data available for this section.'}</div>;
}

const EnergyAuditTabs: FC<EnergyAuditTabsProps> = ({
  activeTab,
  setActiveTab,
  data,
  tabs,
  isFetching,
}) => {
  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`flex-1 px-4 py-3 rounded-t-lg flex items-center gap-2 transition-colors${
              activeTab === tab.key ? ' bg-primary/10' : ' bg-gray-10'
            }`}
            onClick={() => {
              setActiveTab(tab.key);
            }}
          >
            {/* Icon before label */}
            <span className="flex items-center gap-2">
              {tab.icon ? tab.icon(activeTab === tab.key) : null}
              <span
                className={`text-base font-medium ${
                  activeTab === tab.key ? 'text-gray-60' : 'text-gray-60/60'
                }`}
              >
                {tab.label}
              </span>
            </span>
          </button>
        ))}
      </div>
      <div className="h-px w-full bg-primary/20" />
      <CardContainer className="rounded-t-none">
        {isFetching ? (
          <Skeleton count={4} height={24} />
        ) : (
          renderTabContent({ activeTab, data })
        )}
      </CardContainer>
      <span className="text-gray-50 text-opacity-60 text-xs italic mt-2">
        This content is generated with the help of AI. Verify accuracy and
        validate figures before making decisions.
      </span>
    </div>
  );
};

export default EnergyAuditTabs;
